import {
  Component,
  EventEmitter,
  OnInit,
  Output,
  NgZone,
  ViewChild,
  ElementRef, ViewChildren, QueryList, Input, ViewContainerRef, ChangeDetectorRef, OnDestroy, AfterViewInit,
} from '@angular/core';
import { LMLoggerService } from '@services/lm-logger.service';
import { LMSpecService } from '@services/lm-spec.service';
import { LMApplicationMenuSearchService } from './lm-application-menu-search.service';
import { LMErrorUtilService } from '@services/utils/lm-error-util.service';
import { LMContextService } from '@services/lm-context.service';
import { LMBusyIndicatorService } from '@services/lm-busyindicator.service';
import { SohoBusyIndicatorDirective } from 'ids-enterprise-ng';
import { LMUrlUtilService } from '@services/utils/lm-url-util.service';
import { LMHtmlUrlDocComponent } from '@html/lm-html-url-doc.component';
import { LMGlobalPersonalizationService } from '@states/services/lm-global-personalization.service';
import { LMApplicationMenuSubMenuService } from '@components/applicationmenu/submenu/lm-application-menu-submenu.service';
import { LMRouteComponentService } from '@services/lm-route-component.service';
import { LMContextUtilService } from '@utils/lm-context-util.service';
import { Subject, SubscriptionLike as ISubscription } from 'rxjs';
import { LMGlobalUtilService } from '@utils/lm-global-util.service';
import { debounceTime, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'lm-application-menu-search',
  templateUrl: 'lm-application-menu-search.component.html',
  styleUrls: [ 'lm-application-menu-search.component.scss' ],
})
export class LMApplicationMenuSearchComponent implements AfterViewInit, OnInit, OnDestroy {
  @Input() searchString: string;
  @Input() menuSpecsByWebApp: Array<any>; // todo type me!!
  @Input() dataSearchResults: Array<LMApplicationMenuSearchResult>;
  @Input() reportSearchResults: Array<LMApplicationMenuSearchResult>;

  private _menuSearchResultsByWebApp: LMApplicationMenuSearchResults;
  @Input() set menuSearchResultsByWebApp(menuSearchResultsByWebApp: LMApplicationMenuSearchResults) {
    this._menuSearchResultsByWebApp = menuSearchResultsByWebApp;

    // todo ben, pwpatton, khollenbech, is there more efficient way to build the webAppSpecsWithSearchResults
    //   when the menuSearchResultsByWebApp changes?
    this.webAppSpecsWithSearchResults = this.lmApplicationMenuSearchService.buildWebAppSpecsFromSearchResults(
      this.lmContextService.getAccessibleWebApps(), menuSearchResultsByWebApp, this.lmContextService.getWebApp());
  }
  get menuSearchResultsByWebApp(): LMApplicationMenuSearchResults {
    return this._menuSearchResultsByWebApp;
  }

  @Output() menuSearched: EventEmitter<any> = new EventEmitter<any>();
  @Output() menuSearchCleared: EventEmitter<any> = new EventEmitter<any>();

  @ViewChildren('searchMenuHeader') private _searchMenuHeaders: QueryList<ElementRef>;
  @ViewChild(SohoBusyIndicatorDirective, { static: true }) busyIndicator: SohoBusyIndicatorDirective;
  @ViewChild('menuFormDialog', { read: ViewContainerRef, static: true }) placeholder: ViewContainerRef;
  @ViewChild('searchField') searchField: ViewContainerRef;

  public accessibility = {
    announcement: ''
  };

  public webAppSpecsWithSearchResults: LMWebAppSpec[];
  public searchMode: boolean;
  public model = { searchFieldValue: '' };
  public reportTitle: string;
  public dataTitle: string;
  public applicationMenuSearchInfo: LMApplicationMenuSearchInfo;
  private subscriptionArray: Array<ISubscription> = []; // todo: type me!
  private menuItemClicked$ = new Subject<any>();
  public searchFieldText$ = new Subject<string>();
  private destroyer$ = new Subject<void>();
  public testVersion1: boolean;

  constructor(
    private routeComponent: LMRouteComponentService,
    private changeDetectorRef: ChangeDetectorRef,
    private lmSpecService: LMSpecService,
    private lmLoggerService: LMLoggerService,
    private lmApplicationMenuSearchService: LMApplicationMenuSearchService,
    private lmErrorUtilService: LMErrorUtilService,
    private lmContextService: LMContextService,
    private lmBusyIndicatorService: LMBusyIndicatorService,
    private lmUrlUtilService: LMUrlUtilService,
    private lmGlobalPersonalizationService: LMGlobalPersonalizationService,
    private lmApplicationMenuSubMenuService: LMApplicationMenuSubMenuService,
    private lmGlobalUtilService: LMGlobalUtilService,
    private elementRef: ElementRef,
    private zone: NgZone,
    private lmContextUtilService: LMContextUtilService,
  ) {
    this.searchFieldText$.pipe(
      takeUntil(this.destroyer$),
      debounceTime(1000))
      .subscribe(value => {
        this.fireMenuSearchEvent(event);
      });
  }

  ngOnInit() {
    this.lmLoggerService.trace('@ApplicationMenuSearchComponent -> ngOnInit');
    this.searchMode = false;
    this.model.searchFieldValue = this.searchString;
    this.applicationMenuSearchInfo = this.lmApplicationMenuSearchService.getApplicationMenuSearchInfo(this.model.searchFieldValue);
    this.setHeaderTitles();
    this.handleFocus();
  }

  ngAfterViewInit() {

    /**
     * LMCLIENT-37027
     * Trigger keyup event after DOM is finished loading
     * This will cause the "x" clear button to display in the search input without user interaction
     * The normal functionality for the search control only shows the clear button after a key event
     * Whereas in this use case we need it to show after initial load because the search has been pre-populated
     * And the search button closes the search overlay when clicked.
     */
    this.triggerKeyUpEventOnSearch();
  }

  ngOnDestroy() {
    this.subscriptionArray.forEach(subscription => subscription.unsubscribe());
    this.destroyer$.next();
    this.destroyer$.complete();
  }

  get searchMenuHeaders(): QueryList<ElementRef> {
    return this._searchMenuHeaders;
  }

  get limitSearch(): boolean {
    return this.applicationMenuSearchInfo.limitSearch;
  }

  onMenuSearched(event: Event) {
    this.menuSearched.emit(event);
  }

  onMenuSearchCleared(event: Event) {
    this.menuSearchCleared.emit(event);
  }

  fireClearSearchEvent(event) {
    this.searchMode = false;
    this.menuSearchCleared.emit(event);
    this.hideBusyIndicator();
  }

  autoSearchMenu(searchValue: string) {
    this.searchFieldText$.next(searchValue);
  }

  handleFocus() {
    // This needs to be in a timeout to ensure that the search field is set to focus
    this.zone.runOutsideAngular(() => {
      setTimeout(() => {
        const nativeElement = this.elementRef.nativeElement;
        nativeElement.querySelectorAll('input')[0].focus();
        return;
      }, 2);
    });
  }

  fireMenuSearchEvent(event: Event) {
    this.showBusyIndicator();

    this.menuSearchResultsByWebApp = {};
    this.dataSearchResults = [];
    this.reportSearchResults = [];

    this.applicationMenuSearchInfo = this.lmApplicationMenuSearchService.getApplicationMenuSearchInfo(this.model.searchFieldValue);

    // this.changeDetectorRef.markForCheck();

    if (!this.model.searchFieldValue) {
      // if value is blank then fire clearSearchEvent event
      this.fireClearSearchEvent(event);
    } else {
      this.applicationMenuSearch();
    }
  }

  private setHeaderTitles() {
    this.reportTitle = Soho.Locale.translate('SearchReportsTitle');
    this.reportTitle = this.reportSearchResults.length > 0 ? this.reportTitle + ' (' + this.reportSearchResults.length + ')' : this.reportTitle;

    this.dataTitle = Soho.Locale.translate('SearchDataTitle');
    this.dataTitle = this.dataSearchResults.length > 0 ? this.dataTitle + ' (' + this.dataSearchResults.length + ')' : this.dataTitle;
  }

  private applicationMenuSearch() {
    const menuSearchUrl = this.lmApplicationMenuSearchService.getMenuSearchUrl(this.model.searchFieldValue);

    // this.subscriptionArray.push(); // don't need to push into a subscription array as this observable will completed internally with each call.
    this.lmSpecService.getRedirectUrl(menuSearchUrl).subscribe(response => {
      const dataMenus = response.dataModules;
      const reports = response.reports;

      this.menuSearchResultsByWebApp = this.lmApplicationMenuSearchService.matchMultiMenus(this.menuSpecsByWebApp, this.model.searchFieldValue, this.applicationMenuSearchInfo);
      this.dataSearchResults = this.lmApplicationMenuSearchService.matchData(dataMenus);
      this.reportSearchResults = this.lmApplicationMenuSearchService.matchReports(reports);

      this.searchMode = true;

      const event: LMApplicationMenuSearchEvent = {
        searchString:              this.model.searchFieldValue,
        menuSpecsByWebApp:         this.menuSpecsByWebApp,
        menuSearchResultsByWebApp: this.menuSearchResultsByWebApp,
        dataSearchResults:         this.dataSearchResults,
        reportSearchResults:       this.reportSearchResults,
        toggleMenu:                Object.keys(this.menuSearchResultsByWebApp).length > 0 ? 'Applications' : this.reportSearchResults.length > 0 ? 'Reports' : 'Data'
      };
      this.setHeaderTitles();

      this.menuSearched.emit(event);

      this.hideBusyIndicator();
    },
    error => {
      this._handleError(error);
    });
  }

  showBusyIndicator() {
    this.lmBusyIndicatorService.showBusyIndicator(this.busyIndicator);
  }

  hideBusyIndicator() {
    this.lmBusyIndicatorService.hideBusyIndicator(this.busyIndicator);
  }

  openSearchMenuItem($event: any) {
    const anchor: any = $event.currentTarget;
    const menuUrl = anchor.getAttribute('searchMenuUrl');

    const routeComponent: LMRouteComponentService = this.routeComponent;
    this.subscriptionArray.push(this.lmSpecService.getApplicationMenuSpec(menuUrl).subscribe(
      response => {
        if (response.dialogUrl) {
          const personalization = this.lmGlobalPersonalizationService.getGlobalPersonalization();
          if (this.lmContextService.is3_5Content(personalization.appDisplay)) {
            const comp: LMHtmlUrlDocComponent = routeComponent.getHtmlUrlDocComponent();
            comp.runActionFormIn3_5(response.dialogUrl);
          } else {
            this.lmApplicationMenuSubMenuService.requestActionFromPageMenu(response.dialogUrl, response.resourceId, this.placeholder);
          }
        } else if (response.redirectUrl) {
          this.lmContextService.setClearNextPageState(true);
          const url = response.redirectUrl;
          const noDefaultPortUrl = this.lmUrlUtilService.stripDefaultPort(url);
          const host = this.lmContextUtilService.getLocation(noDefaultPortUrl).host;
          if (response.external) {
            this.lmUrlUtilService.openExternalUrl(response.redirectUrl);
          } else if (response.embedded) {
            if ((url.length > 0 && host !== window.location.host) || (response.redirectUrl.indexOf('.html') >= 0)) {
              const isHtmlFragment = response.redirectUrl.indexOf('standalone=true') < 0;
              const webapp = this.lmContextService.getContext().webApp;
              const dataArea = this.lmContextService.getContext().urlPdlOrDa;
              const autoLoadUrl = dataArea + '/' + webapp + '/menu/url?autoLoadUrl=' + encodeURIComponent(response.redirectUrl) + (isHtmlFragment ? '&isHtmlFragment=true' : '');
              this.lmGlobalUtilService.navigateByUrl(autoLoadUrl);
            } else {
              this.lmGlobalUtilService.navigateByUrl(response.redirectUrl);
            }
          } else {
            if ((url.length > 0 && host !== window.location.host) || (response.redirectUrl.indexOf('.html') >= 0)) {
              window.location.href = response.redirectUrl;
            } else {
              this.lmGlobalUtilService.navigateByUrl(response.redirectUrl);
            }
          }
        }
      },
      error => {
        this._handleError(error);
      }));
  }

  public onMenuItemClicked(menuItem: any) {
    this.menuItemClicked$.next(menuItem);
  }

  public openSearchItemLink($event: any) {
    const anchor: any = $event.currentTarget;
    const menuUrl = anchor.getAttribute('searchMenuUrl');
    const resourceId = anchor.getAttribute('resourceId');

    const actionParam = this.lmUrlUtilService.getUrlParam(menuUrl, 'action');
    if (actionParam) {
      const personalization = this.lmGlobalPersonalizationService.getGlobalPersonalization();
      if (this.lmContextService.is3_5Content(personalization.appDisplay)) {
        const comp: LMHtmlUrlDocComponent = this.routeComponent.getHtmlUrlDocComponent();
        comp.runActionFormIn3_5(menuUrl);
      } else {
        this.lmApplicationMenuSubMenuService.requestActionFromPageMenu(menuUrl, resourceId, this.placeholder);
      }
    } else {
      this.lmGlobalUtilService.navigateByUrl(menuUrl);
    }
  }

  private _handleError(error: any): void {
    this.lmErrorUtilService.handleUnexpectedError(error);
  }

  private triggerKeyUpEventOnSearch() {
    const event = new KeyboardEvent('keyup', { bubbles:true });
    setTimeout(() => this.searchField.element.nativeElement.dispatchEvent(event));
  }

  public dataAutomationId(menuItem: LMBannerSearchResult) {
    // const automationId = menuItem.id.replace(/(_prd_)|(_)/g, '-');
    // return automationId + '-searchresult';
    return `lm-${menuItem.automationId}-searchresult`;
  }
}
